import { useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { cld, money, CATEGORY_LABEL } from "@/lib/api";

const CartLine = ({ i }) => {
  const { remove, setQty } = useCart();
  return (
    <div className="flex gap-5" data-testid={`cart-item-${i.key}`}>
      <img src={cld(i.image, "w_200,q_auto,f_auto")} alt={i.title} className="h-28 w-[88px] shrink-0 bg-[#F2EEE6] object-cover" />
      <div className="flex flex-1 flex-col">
        <div className="flex justify-between gap-3">
          <div>
            <p className="font-display text-xl leading-tight">{i.title}</p>
            <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#8C7A5B]">{CATEGORY_LABEL[i.category]}</p>
            {!i.isOriginal && <p className="mt-1 text-xs text-[#5A626A]">{i.variantLabel}</p>}
          </div>
          <button aria-label="Remove" data-testid={`cart-remove-${i.key}`} onClick={() => remove(i.key)} className="h-fit text-[#8A8780] hover:text-[#1A1A1A]">
            <X size={16} strokeWidth={1.25} />
          </button>
        </div>
        <div className="mt-auto flex items-center justify-between pt-3">
          {i.isOriginal ? (
            <span className="text-xs text-[#5A626A]">One of one</span>
          ) : (
            <div className="flex items-center border border-[#E5E0D8]">
              <button className="p-2" data-testid={`cart-qty-dec-${i.key}`} onClick={() => setQty(i.key, i.quantity - 1)}><Minus size={12} /></button>
              <span className="w-8 text-center text-sm" data-testid={`cart-qty-${i.key}`}>{i.quantity}</span>
              <button className="p-2" data-testid={`cart-qty-inc-${i.key}`} onClick={() => setQty(i.key, i.quantity + 1)}><Plus size={12} /></button>
            </div>
          )}
          <span className="text-sm">{money(i.price * i.quantity)}</span>
        </div>
      </div>
    </div>
  );
};

export const CartDrawer = () => {
  const { items, open, setOpen, subtotal } = useCart();
  const navigate = useNavigate();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="flex w-full flex-col rounded-none border-l border-[#E5E0D8] bg-[#FDFBF7] p-8 sm:max-w-md">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-4xl font-light">Your bag</SheetTitle>
          <SheetDescription className="text-xs text-[#5A626A]">Prices in Australian dollars.</SheetDescription>
        </SheetHeader>
        {items.length === 0 ? (
          <p className="mt-12 text-sm text-[#5A626A]" data-testid="cart-empty">Your bag is empty.</p>
        ) : (
          <div className="mt-10 flex-1 space-y-8 overflow-y-auto pr-1">
            {items.map((i) => <CartLine key={i.key} i={i} />)}
          </div>
        )}
        {items.length > 0 && (
          <div className="border-t border-[#E5E0D8] pt-6">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{money(subtotal)}</span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-[#5A626A]">Shipping, insurance and codes are calculated at checkout. Originals ship free within Australia.</p>
            <button className="btn-primary mt-6 w-full" data-testid="checkout-button" onClick={() => { setOpen(false); navigate("/checkout"); }}>
              Checkout
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
